import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { useSurveyDetailOptions, useSubmitSurveyMutation, useSaveAnswerMutation } from "./surveys.api";
import { AnswerValue } from "./surveys.types";
import Checkbox from "@/pages/get-don/components/tests/test-types/CheckBox";
import MultipleChoose from "@/pages/get-don/components/tests/test-types/MultipleChoise";
import TextAnswer from "./components/question-types/TextAnswer";
import RatingInput from "./components/question-types/RatingInput";
import Ordering from "./components/question-types/Ordering";
import PageWrapper from "@/components/PageWrapper";
import BaseButton from "@/components/base/button/BaseButton";
import DonDialog from "@/components/base/DonDialog";
import showCustomToast from "@/shared/utils/showCustomToast";
import { boom } from "@/lib/exposion";
import { ChevronLeft, ChevronRight, ClipboardCheck, Clock } from "lucide-react";
import IconDon from "@/assets/icons/don.svg?react";
import IconDonLinear from "@/assets/icons/don-filled-linear.svg?react";
import IconNotes from "@/assets/icons/notes.svg?react";
import { useTranslation } from "react-i18next";

type Phase = "intro" | "questions" | "success";

export default function SurveyDetailPage() {
  const { surveyId } = useParams<{ surveyId: string }>();
  const navigate = useNavigate();
  const { data: survey, isLoading } = useQuery(useSurveyDetailOptions(surveyId));
  const submitMutation = useSubmitSurveyMutation();
  const saveAnswerMutation = useSaveAnswerMutation();

  const [phase, setPhase] = useState<Phase>("intro");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, AnswerValue>>({});
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [donResult, setDonResult] = useState<{ awarded: boolean; amount: number }>({
    awarded: false,
    amount: 0,
  });

  const { t } = useTranslation();
  const updateAnswer = useCallback(
    (questionId: string, value: Partial<AnswerValue>) => {
      setAnswers((prev) => ({
        ...prev,
        [questionId]: { ...prev[questionId], ...value },
      }));
    },
    []
  );

  useEffect(() => {
    if (survey?.is_completed && phase !== "success")
      navigate("/profile/surveys", { replace: true });
  }, [survey]);

  // Oldingi javoblarni tiklash
  useEffect(() => {
    if (!survey || survey.is_completed) return;
    const restored: Record<string, AnswerValue> = {};
    for (const q of survey.questions) {
      if (q.user_answer) {
        restored[q.id] = {
          selected_option_id: q.user_answer.selected_option_id,
          selected_option_ids: q.user_answer.selected_option_ids,
          text_answer: q.user_answer.text_answer,
          rating_value: q.user_answer.rating_value,
          ordering: q.user_answer.ordering_answer,
        };
      }
    }
    if (Object.keys(restored).length > 0) {
      setAnswers(restored);
      // Oxirgi javob berilgan savoldan davom etish
      const lastAnsweredIndex = survey.questions.reduce((maxIdx, q, idx) => {
        return restored[q.id] ? idx : maxIdx;
      }, 0);
      setCurrentIndex(lastAnsweredIndex);
    }
  }, [survey]);

  const hasAnswer = (questionId: string) => {
    const a = answers[questionId];
    if (!a) return false;
    return !!(
      a.selected_option_id ||
      (a.selected_option_ids && a.selected_option_ids.length > 0) ||
      (a.text_answer && a.text_answer.trim().length > 0) ||
      (a.rating_value !== undefined && a.rating_value >= 0) ||
      (a.ordering && a.ordering.length > 0)
    );
  };

  const saveAnswer = (questionId: string) => {
    if (!survey || !hasAnswer(questionId)) return;
    const answer = answers[questionId];
    saveAnswerMutation.mutate({
      surveyId: survey.id,
      answer: {
        question_id: questionId,
        selected_option_id: answer.selected_option_id,
        selected_option_ids: answer.selected_option_ids,
        text_answer: answer.text_answer,
        rating_value: answer.rating_value,
        ordering: answer.ordering ? answer.ordering.split(",") : undefined,
      },
    });
  };

  const allRequiredAnswered = () => {
    if (!survey) return false;
    return survey.questions.every((q) => !q.is_required || hasAnswer(q.id));
  };

  // Keyingi javob berilmagan savolni topadi. Oldin skip qilinganlar
  // bo'lsa ham topish uchun oxirigacha yetgach boshiga qaytariladi.
  const findNextUnanswered = (fromIdx: number): number => {
    if (!survey) return -1;
    const qs = survey.questions;
    for (let i = fromIdx + 1; i < qs.length; i++) {
      if (!hasAnswer(qs[i].id)) return i;
    }
    for (let i = 0; i < fromIdx; i++) {
      if (!hasAnswer(qs[i].id)) return i;
    }
    return -1;
  };

  const handleNext = () => {
    if (!survey) return;
    const question = survey.questions[currentIndex];
    if (question) saveAnswer(question.id);
    // Barcha majburiy savollar javob berilgan bo'lsa — avtomatik tugatish.
    if (allRequiredAnswered()) {
      handleSubmit();
      return;
    }
    // Keyingi javobsiz savolga sakrash — javob berilgan savolga qaytmaydi.
    const nextIdx = findNextUnanswered(currentIndex);
    setCurrentIndex(nextIdx !== -1 ? nextIdx : currentIndex + 1);
  };

  const handleSubmit = () => {
    if (!survey) return;

    const missing: number[] = [];
    survey.questions.forEach((q, idx) => {
      if (q.is_required && !hasAnswer(q.id)) missing.push(idx);
    });

    if (missing.length > 0) {
      setCurrentIndex(missing[0]);
      showCustomToast(
        t("survey_required_questions_warning", { count: missing.length }),
        "error"
      );
      return;
    }

    const currentQuestion = survey.questions[currentIndex];
    if (currentQuestion && hasAnswer(currentQuestion.id)) {
      const answer = answers[currentQuestion.id];
      saveAnswerMutation.mutate(
        {
          surveyId: survey.id,
          answer: {
            question_id: currentQuestion.id,
            selected_option_id: answer.selected_option_id,
            selected_option_ids: answer.selected_option_ids,
            text_answer: answer.text_answer,
            rating_value: answer.rating_value,
            ordering: answer.ordering ? answer.ordering.split(",") : undefined,
          },
        },
        {
          onSuccess: () => {
            submitMutation.mutate(survey.id, {
              onSuccess: (result) => {
                setDonResult({ awarded: result.don_awarded, amount: result.don_amount });
                setPhase("success");
                if (result.don_awarded && result.don_amount > 0) boom();
              },
              onError: () =>
                showCustomToast(t("error_occurred") || "Xatolik yuz berdi", "error"),
            });
          },
          onError: () =>
            showCustomToast(t("error_occurred") || "Xatolik yuz berdi", "error"),
        }
      );
    } else {
      submitMutation.mutate(survey.id, {
        onSuccess: (result) => {
          setDonResult({ awarded: result.don_awarded, amount: result.don_amount });
          setPhase("success");
          if (result.don_awarded && result.don_amount > 0) boom();
        },
        onError: () =>
          showCustomToast(t("error_occurred") || "Xatolik yuz berdi", "error"),
      });
    }
  };

  // ─── Loading ───
  if (isLoading || !survey) {
    return (
      <PageWrapper backPath="/profile/surveys">
        <div className="fixed inset-0 bg-dark flex flex-col pt-safe-top">
          <div className="flex-1 flex flex-col items-center justify-center px-4">
            {/* Icon */}
            <div className="skeleton w-20 h-20 rounded-full mb-5" />
            {/* Title */}
            <div className="skeleton h-7 w-48 rounded mb-2" />
            {/* Description */}
            <div className="skeleton h-4 w-56 rounded mb-1" />
            <div className="skeleton h-4 w-44 rounded mb-6" />
            {/* Info cards */}
            <div className="flex gap-2 w-full">
              <div className="flex-1 rounded-xl border border-white/[0.04] bg-gray-4 px-3 py-4 flex flex-col items-center gap-1.5">
                <div className="skeleton h-3 w-12 rounded" />
                <div className="skeleton h-4 w-16 rounded" />
              </div>
              <div className="flex-1 rounded-xl border border-white/[0.04] bg-gray-4 px-3 py-4 flex flex-col items-center gap-1.5">
                <div className="skeleton h-3 w-14 rounded" />
                <div className="skeleton h-4 w-8 rounded" />
              </div>
            </div>
          </div>
          {/* Bottom button */}
          <div className="p-4 pb-safe-bottom">
            <div className="skeleton h-12 w-full rounded-xl" />
          </div>
        </div>
      </PageWrapper>
    );
  }

  // ─── Success ───
  if (phase === "success") {
    const hasReward = donResult.awarded && donResult.amount > 0;
    return (
      <div className="fixed inset-0 z-40 bg-dark flex flex-col items-center justify-between py-safe-top pb-safe-bottom">
        <div className="flex flex-col items-center w-full px-4 flex-1 justify-center">
          {hasReward && (
            <div className="flex items-center justify-center mb-6">
              <IconDon width="48px" height="48px" className="text-orange-100" />
              <p className="text-orange-100 text-5xl font-bold leading-130">
                {Math.floor(donResult.amount)}
              </p>
            </div>
          )}
          <h2 className="text-2xl font-bold text-white mb-2 text-center">
            {t("survey_thank_you") || "Minnatdorchilik bildiramiz!"}
          </h2>
          <p className="text-center font-normal text-gray-1 leading-140 text-sm px-4">
            {t("survey_thank_you_description") ||
              "Sizning fikr va takliflaringiz bizni yanada rivojlanishimizda muhim rol o'ynaydi."}
          </p>
        </div>
        <div className="w-full px-4 pb-4">
          <BaseButton
            text={
              hasReward
                ? t("survey_claim_reward") || "Mukofotni olish"
                : t("go_home") || "Asosiyga qaytish"
            }
            variant={hasReward ? "premium" : "primary"}
            className="w-full text-sm leading-130 font-semibold text-black"
            onClick={() => navigate("/profile/surveys")}
          />
        </div>
      </div>
    );
  }

  // ─── Intro ───
  const hasPreviousAnswers = survey.answered_count > 0;

  if (phase === "intro") {
    const infoCards: { icon: React.ReactNode; label: string; value: string; valueColor: string }[] = [];
    if (survey.don_reward > 0) {
      infoCards.push({
        icon: <IconDonLinear className="w-5 h-5 text-orange-100" />,
        label: "DON",
        value: `+${Math.floor(survey.don_reward)} DON`,
        valueColor: "text-orange-100",
      });
    }
    infoCards.push({
      icon: <IconNotes className=" text-blue w-5 h-5" />,
      label: t("questions"),
      value: String(survey.questions.length),
      valueColor: "text-blue",
    });

    return (
      <PageWrapper backPath="/profile/surveys">
        <div className="fixed inset-0 bg-dark flex flex-col pt-safe-top">
          <div className="flex-1 flex flex-col items-center justify-center px-4">
            <div className="w-20 h-20 rounded-full bg-blue/15 flex-center mb-5">
              <ClipboardCheck className="w-10 h-10 text-blue" />
            </div>
            <h2 className="text-[22px] font-bold text-white mb-2 text-center">
              {survey.title || (t("survey") || "So'rovnoma!")}
            </h2>
            <p className="text-sm text-gray-1 text-center leading-140 max-w-[280px]">
              {survey.description ||
                (t("survey_default_description") ||
                  "So'rovnomani yakunlab, xizmatlarimizni yaxshilashga hissa qo'shing")}
            </p>

            {/* Info cards */}
            <div
              className="w-full mt-6 max-w-sm"
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(${infoCards.length}, 1fr)`,
                gap: "10px",
              }}
            >
              {infoCards.map((card, idx) => (
                <SurveyInfoCard
                  key={idx}
                  icon={card.icon}
                  label={card.label}
                  value={card.value}
                  valueColor={card.valueColor}
                />
              ))}
            </div>

            {hasPreviousAnswers && (
              <div className="mt-3 w-full max-w-sm rounded-xl border border-white/[0.04] bg-gray-4 px-4 py-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 text-xs text-gray-1 font-medium">
                    <Clock className="w-3.5 h-3.5" />
                    <span>{t("survey_in_progress")}</span>
                  </div>
                  <span className="text-xs font-bold text-orange-100">
                    {survey.answered_count}/{survey.questions.length}
                  </span>
                </div>
                <div className="w-full h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full bg-orange-100 transition-all"
                    style={{
                      width: `${(survey.answered_count / survey.questions.length) * 100}%`,
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="p-4 pb-safe-bottom">
            <BaseButton
              text={
                hasPreviousAnswers
                  ? t("survey_continue") || "Davom ettirish"
                  : t("survey_start") || "Boshlash"
              }
              className="w-full"
              onClick={() => setPhase("questions")}
            />
          </div>
        </div>
      </PageWrapper>
    );
  }

  // ─── Questions ───
  const questions = survey.questions;
  const totalQuestions = questions.length;
  const currentQuestion = questions[currentIndex];
  const isLastQuestion = currentIndex === totalQuestions - 1;
  // Bar foydalanuvchi bergan javoblar soniga qarab to'ladi — joriy
  // savol indeksiga emas. Shunda "skip" bilan oldinga o'tish progressni
  // soxta to'ldirmaydi.
  const answeredCount = questions.filter((q) => hasAnswer(q.id)).length;
  const progress =
    totalQuestions > 0 ? (answeredCount / totalQuestions) * 100 : 0;
  const currentAnswer = currentQuestion ? answers[currentQuestion.id] : undefined;
  // User oldin skip qilgan savolga qaytarilib uni to'ldirgach, boshqa
  // savollar allaqachon javoblangan bo'lsa — tugma darhol "Finish" bo'lsin.
  const canFinishNow = allRequiredAnswered();

  if (!currentQuestion) return null;

  const donOptions = currentQuestion.options.map((o) => ({ id: o.id, title: o.text }));

  return (
    <PageWrapper>
      <div className="pb-24">
        <div className="flex items-center justify-center px-4 py-3">
          <h1 className="text-sm font-semibold text-white">
            {t("survey") || "So'rovnoma"}
          </h1>
        </div>

        <div className="container">
          <div className="p-3 w-full rounded-xl bg-gray-4 mb-4">
            <div className="flex items-center mb-2 justify-between">
              <h1 className="text-white text-lg leading-130 font-semibold">
                {t("question") || "Savol"} {currentIndex + 1}/{totalQuestions}
              </h1>
              {/*{currentQuestion.is_required && (*/}
              {/*  <span className="text-3xl text-red p-1 bg-red/10 rounded-full">*/}
              {/*    <IconAsterisk width={15} stroke='5' height={15} />*/}
              {/*  </span>*/}
              {/*)}*/}
            </div>
            <div className="w-full mb-3">
              <div className="bg-white/[0.06] w-full h-1.5 rounded-full overflow-hidden">
                <div
                  style={{ width: `${progress}%` }}
                  className="h-1.5 rounded-full bg-orange-100 transition-all"
                />
              </div>
            </div>
            <h2 className="leading-140 text-lg font-semibold text-white">
              {currentQuestion.text}
            </h2>
          </div>

          {currentQuestion.question_type === "SINGLE_CHOICE" && (
            <Checkbox
              options={donOptions}
              value={currentAnswer?.selected_option_id || ""}
              onChange={(id) => updateAnswer(currentQuestion.id, { selected_option_id: id })}
            />
          )}
          {currentQuestion.question_type === "MULTIPLE_CHOICE" && (
            <MultipleChoose
              options={donOptions}
              value={currentAnswer?.selected_option_ids || []}
              onChange={(ids) => updateAnswer(currentQuestion.id, { selected_option_ids: ids })}
            />
          )}
          {currentQuestion.question_type === "TEXT" && (
            <TextAnswer
              value={currentAnswer?.text_answer}
              onChange={(text) => updateAnswer(currentQuestion.id, { text_answer: text })}
              maxLength={currentQuestion.max_length}
            />
          )}
          {currentQuestion.question_type === "RATING" && (
            <RatingInput
              value={currentAnswer?.rating_value}
              onChange={(rating) => updateAnswer(currentQuestion.id, { rating_value: rating })}
            />
          )}
          {currentQuestion.question_type === "ORDERING" && (
            <Ordering
              options={currentQuestion.options}
              value={currentAnswer?.ordering}
              onChange={(ids) => updateAnswer(currentQuestion.id, { ordering: ids })}
            />
          )}
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-dark p-4 pb-safe-bottom flex gap-2">
        <BaseButton
          text={currentIndex === 0 ? t("exit") : t("back")}
          prefix={<ChevronLeft className="w-4 h-4" />}
          variant="secondary"
          className="flex-1"
          onClick={() => {
            const question = survey.questions[currentIndex];
            if (question) saveAnswer(question.id);
            if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
            else setShowCancelDialog(true);
          }}
        />
        {canFinishNow || isLastQuestion ? (
          <BaseButton
            text={t("finish")}
            suffix={<ChevronRight className="w-4 h-4" />}
            variant="premium"
            className="flex-1 text-black"
            onClick={handleSubmit}
            isLoading={submitMutation.isPending}
          />
        ) : (
          <BaseButton
            text={t("continue")}
            suffix={<ChevronRight className="w-4 h-4" />}
            className="flex-1"
            onClick={handleNext}
          />
        )}
      </div>

      <DonDialog
        isOpen={showCancelDialog}
        onOpenChange={setShowCancelDialog}
        trigger={<></>}
        title={t("survey_exit_title") || "So'rovnoma hali tugagani yo'q"}
        description={
          t("survey_exit_description") ||
          "Yana ozgina qoldi, so'rovnomamizda ishtirok eting va sizga atalgan sovg'amizga ega bo'ling. Unutmang sizning fikringiz biz uchun juda muhim"
        }
        footer={
          <div className="flex flex-col gap-2 w-full">
            <BaseButton
              text={t("continue") || "Davom etish"}
              onClick={() => setShowCancelDialog(false)}
            />
            <BaseButton
              text={t("exit")}
              variant="secondary"
              onClick={() => {
                const question = survey.questions[currentIndex];
                if (question) saveAnswer(question.id);
                navigate("/profile/surveys");
              }}
            />
          </div>
        }
      />
    </PageWrapper>
  );
}

function SurveyInfoCard({
  icon,
  label,
  value,
  valueColor = "text-white",
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  valueColor?: string;
}) {
  return (
    <div className="w-full rounded-xl border border-white/[0.04] bg-gray-4 px-4 py-4 flex flex-col items-center gap-2">
      <div className="flex items-center gap-2 text-sm text-white font-medium">
        {icon}
        <span>{label}</span>
      </div>
      <span className={`text-base font-bold leading-130 ${valueColor}`}>
        {value}
      </span>
    </div>
  );
}
